import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import Header from "../Header";
import Footer from "../Footer";

const CardDetails = () => {
    const { id } = useParams();
    const [tour, setTour] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openIndex, setOpenIndex] = useState(null);
    const [isScrolled, setIsScrolled] = useState(false);

    const toggleAccordion = (index) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    useEffect(() => {
        axios.get(`https://kyrgyz-adventure-travel.com/api/v1/tours/tours/${id}/`)
            .then(response => setTour(response.data))
            .catch(error => {
                console.error('Error loading data:', error);
                setError("Error loading data");
            })
            .finally(() => setLoading(false));
    }, [id]);

    useEffect(() => {
        const handleScroll = () => setIsScrolled(window.scrollY > 50);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    if (loading) return (
        <div className="bg-dark min-h-screen flex items-center justify-center">
            <p className="font-unbounded text-white/50 text-sm">Loading...</p>
        </div>
    );
    if (error) return (
        <div className="bg-dark min-h-screen flex items-center justify-center">
            <p className="font-manrope text-red-400">{error}</p>
        </div>
    );

    return (
        <div className="bg-dark min-h-screen">
            {/* Hero */}
            <div className="relative">
                {tour?.banner && (
                    <img
                        src={tour.banner.replace(/^http:/, 'https:')}
                        alt={tour.title}
                        className="w-full max-h-[80vh] object-cover object-bottom"
                    />
                )}
                <div className="absolute inset-0 bg-black/30" />
                <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-dark to-transparent" />
                <Header isScrolled={isScrolled} />
                <div className="absolute bottom-16 left-0 right-0 z-10">
                    <div className="container">
                        <h1 className="font-unbounded font-bold text-4xl md:text-6xl text-white max-w-3xl leading-tight">
                            {tour.title}
                        </h1>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="container py-16">
                <div className="flex flex-col lg:flex-row gap-8">
                    {/* Main Content */}
                    <div className="w-full lg:w-2/3 flex flex-col gap-8">
                        {/* Itinerary */}
                        <div className="card-dark p-8">
                            <h3 className="font-unbounded font-bold text-xl text-white mb-6">Itinerary</h3>
                            <div className="flex flex-col gap-2">
                                {tour?.itineraries?.map((itinerary, index) => (
                                    <div key={itinerary.id} className="border border-white/10 rounded-2xl overflow-hidden">
                                        <button
                                            className="flex items-center justify-between w-full py-4 px-5 text-left hover:bg-white/5 transition-colors duration-200"
                                            onClick={() => toggleAccordion(index)}
                                        >
                                            <span className="font-manrope font-semibold text-white">
                                                <span className="text-gold font-unbounded text-sm mr-3">Day {index + 1}</span>
                                                {itinerary.title}
                                            </span>
                                            <span className="text-gold text-xl font-light ml-4">
                                                {openIndex === index ? "−" : "+"}
                                            </span>
                                        </button>
                                        {openIndex === index && (
                                            <div className="px-5 pb-5 bg-dark-3">
                                                <img
                                                    src={itinerary.image?.replace(/^http:/, 'https:')}
                                                    alt={itinerary.title}
                                                    className="w-full max-h-80 object-contain rounded-2xl mb-4"
                                                />
                                                <div className="font-manrope text-white/70 text-sm leading-relaxed flex flex-col gap-2">
                                                    {itinerary.description.split('\n').filter(p => p.trim()).map((paragraph, i) => (
                                                        <p key={i}>{paragraph}</p>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Included / Excluded */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            <div className="card-dark p-6">
                                <h3 className="font-unbounded font-bold text-base text-white mb-5">Included</h3>
                                <div className="flex flex-col gap-3">
                                    {tour?.inclusions?.map((item, index) => (
                                        <div key={index} className="flex items-start gap-3">
                                            <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0 mt-0.5">
                                                <path d="M12.72 6.79L8.43001 11.09L6.78 9.44C6.69036 9.33532 6.58004 9.2503 6.45597 9.19027C6.33191 9.13025 6.19678 9.09652 6.05906 9.0912C5.92134 9.08588 5.78401 9.10909 5.65568 9.15936C5.52736 9.20964 5.41081 9.28589 5.31335 9.38335C5.2159 9.4808 5.13964 9.59735 5.08937 9.72568C5.03909 9.854 5.01589 9.99133 5.02121 10.1291C5.02653 10.2668 5.06026 10.4019 5.12028 10.526C5.1803 10.65 5.26532 10.7604 5.37 10.85L7.72 13.21C7.81344 13.3027 7.92426 13.376 8.0461 13.4258C8.16794 13.4755 8.2984 13.5008 8.43001 13.5C8.69234 13.4989 8.94374 13.3947 9.13 13.21L14.13 8.21C14.2237 8.11704 14.2981 8.00644 14.3489 7.88458C14.3997 7.76272 14.4258 7.63201 14.4258 7.5C14.4258 7.36799 14.3997 7.23728 14.3489 7.11542C14.2981 6.99356 14.2237 6.88296 14.13 6.79C13.9426 6.60375 13.6892 6.49921 13.425 6.49921C13.1608 6.49921 12.9074 6.60375 12.72 6.79ZM10 0C8.02219 0 6.08879 0.58649 4.4443 1.6853C2.79981 2.78412 1.51809 4.3459 0.761209 6.17317C0.00433284 8.00043 -0.193701 10.0111 0.192152 11.9509C0.578004 13.8907 1.53041 15.6725 2.92894 17.0711C4.32746 18.4696 6.10929 19.422 8.0491 19.8079C9.98891 20.1937 11.9996 19.9957 13.8268 19.2388C15.6541 18.4819 17.2159 17.2002 18.3147 15.5557C19.4135 13.9112 20 11.9778 20 10C20 8.68678 19.7413 7.38642 19.2388 6.17317C18.7363 4.95991 17.9997 3.85752 17.0711 2.92893C16.1425 2.00035 15.0401 1.26375 13.8268 0.761205C12.6136 0.258658 11.3132 0 10 0ZM10 18C8.41775 18 6.87104 17.5308 5.55544 16.6518C4.23985 15.7727 3.21447 14.5233 2.60897 13.0615C2.00347 11.5997 1.84504 9.99113 2.15372 8.43928C2.4624 6.88743 3.22433 5.46197 4.34315 4.34315C5.46197 3.22433 6.88743 2.4624 8.43928 2.15372C9.99113 1.84504 11.5997 2.00346 13.0615 2.60896C14.5233 3.21447 15.7727 4.23984 16.6518 5.55544C17.5308 6.87103 18 8.41775 18 10C18 12.1217 17.1572 14.1566 15.6569 15.6569C14.1566 17.1571 12.1217 18 10 18Z" fill="#CE2B37"/>
                                            </svg>
                                            <span className="font-manrope text-white/70 text-sm">{item.item}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="card-dark p-6">
                                <h3 className="font-unbounded font-bold text-base text-white mb-5">Not included</h3>
                                <div className="flex flex-col gap-3">
                                    {tour?.exclusions?.map((item, index) => (
                                        <div key={index} className="flex items-start gap-3">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0 mt-0.5">
                                                <circle cx="12" cy="12" r="10" stroke="#ef4444" strokeWidth="2" fill="none"/>
                                                <path d="M16 8L8 16M8 8L16 16" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                            <span className="font-manrope text-white/70 text-sm">{item.item}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Map */}
                        {tour?.map && (
                            <div className="card-dark p-6">
                                <h3 className="font-unbounded font-bold text-base text-white mb-5">Map</h3>
                                <img src={tour.map?.replace(/^http:/, 'https:')} alt="Map" className="rounded-2xl w-full" />
                            </div>
                        )}
                    </div>

                    {/* Sidebar */}
                    <div className="w-full lg:w-1/3">
                        <div className="card-dark p-7 sticky top-24">
                            <h2 className="font-unbounded font-bold text-lg text-white pb-5 border-b border-white/10 mb-5">
                                {tour?.title}
                            </h2>
                            <p className="font-manrope text-white/60 text-sm leading-relaxed mb-6">
                                {tour?.description}
                            </p>

                            <div className="flex flex-col gap-4">
                                {[
                                    { label: 'Season', value: tour?.season },
                                    { label: 'Duration', value: tour?.duration?.replace("\\", " | ") },
                                ].map(item => item.value && (
                                    <div key={item.label} className="bg-dark rounded-2xl p-4">
                                        <p className="font-unbounded text-white/40 text-xs uppercase tracking-wider mb-1">
                                            {item.label}
                                        </p>
                                        <p className="font-manrope font-semibold text-white">{item.value}</p>
                                    </div>
                                ))}

                                {tour?.price && (
                                    <div className="bg-it-green/10 border border-it-green/30 rounded-2xl p-4">
                                        <p className="font-unbounded text-it-green/70 text-xs uppercase tracking-wider mb-2">
                                            Price
                                        </p>
                                        {tour.price.split(",").map((item, index) => (
                                            <p key={index} className="font-unbounded font-bold text-it-green text-lg">
                                                {item.trim()}
                                            </p>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default CardDetails;
